import { useEffect, useState } from "react";
import { Eye, CheckCheck, MessageSquare } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import {
  useUpdateAlertStatus,
  useUpdateAlertNote,
} from "@/features/alert/hooks";
import {
  useApproveChangeRequest,
  useRejectChangeRequest,
} from "@/features/order/hooks";
import { useCheckin, useDecideReturn } from "@/features/checkin/hooks";
import { selectLatestActiveReturnLot } from "@/features/checkin/itemDisplay";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { ApiError } from "@/api/client";
import { timeAgo } from "@/lib/timeAgo";
import type { Alert, AlertStatus } from "@/features/alert/types";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { getOperationalCapabilities } from "@/features/auth/operationalCapabilities";
import { ReturnResolutionModal } from "@/components/checkin/ReturnResolutionModal";
import { InlineActionButton } from "@/components/ui/InlineActionButton";
import { operationalTheme } from "@/components/operational/operationalStyles";
import { AlertActions } from "./AlertActions";
import {
  getAlertActionMode,
  getAlertActionVisibility,
  getAlertDestination,
  getAlertTypeLabel,
  getSeenTransitionStatus,
} from "./alertCardRules";

const PRIORITY_COLORS: Record<string, string> = {
  high: "#ef4444",
  medium: "#d97706",
  low: "var(--login-muted)",
};

const COLLAPSE_STYLE = {
  overflow: "hidden",
  transition: "max-height 0.2s ease, opacity 0.18s ease, transform 0.18s ease",
} satisfies React.CSSProperties;

function notificationActionError(error: unknown, alert: Alert) {
  if (error instanceof ApiError) {
    if (error.code === "order_not_found") {
      return alert.actionable
        ? "Pedido indisponível. Use Limpar encerradas para remover este aviso."
        : "Pedido indisponível. Marque este aviso como lido em Novas.";
    }
    if (error.status === 404) {
      return "Esta notificação não está mais disponível. Atualize a lista.";
    }
    if (error.status === 409) {
      return "Esta notificação mudou. Atualize a lista antes de tentar novamente.";
    }
  }
  if (error instanceof TypeError) {
    return "Falha de conexão. Verifique sua rede e tente novamente.";
  }
  return getUserFacingErrorMessage(
    error,
    "Não foi possível atualizar a notificação. Tente novamente.",
  );
}

function sleep(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function shouldDismiss(
  currentFilter: AlertStatus | "all" | undefined,
  nextStatus: AlertStatus,
) {
  return (
    !!currentFilter && currentFilter !== "all" && currentFilter !== nextStatus
  );
}

export function AlertCard({
  alert,
  summaryInHeader = false,
  activeStatusFilter,
  onResolutionSuccess,
  onNestedDialogChange,
  onMutationPendingChange,
  bulkPending,
}: {
  alert: Alert;
  summaryInHeader?: boolean;
  activeStatusFilter?: AlertStatus | "all";
  onResolutionSuccess?: (result: string) => void;
  onNestedDialogChange?: (open: boolean) => void;
  onMutationPendingChange?: (pending: boolean) => void;
  bulkPending: boolean;
}) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  const [returnResolutionOpen, setReturnResolutionOpen] = useState(false);
  const [returnResolutionDecision, setReturnResolutionDecision] = useState<
    "SUBSTITUIDO" | "DEVOLVIDO"
  >("SUBSTITUIDO");
  const [noteText, setNoteText] = useState(alert.note ?? "");
  const [error, setError] = useState<string | null>(null);
  const [isDismissing, setIsDismissing] = useState(false);

  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const updateStatus = useUpdateAlertStatus();
  const updateNote = useUpdateAlertNote();
  const approveCR = useApproveChangeRequest();
  const rejectCR = useRejectChangeRequest();
  const {
    data: checkin,
    isLoading: isCheckinLoading,
    isError: isCheckinError,
    isFetchedAfterMount: isCheckinFresh,
    refetch: refetchCheckin,
  } = useCheckin(alert.actionable ? (alert.checkin_id ?? "") : "", true);
  const decideReturn = useDecideReturn(alert.checkin_id ?? "");

  const isReturnAlert =
    alert.alert_type === "return_decision" &&
    !!alert.checkin_id &&
    !!alert.checkin_item_id;
  const isSolved = alert.status === "solved";
  const { canManageOrders, canManageCheckins } =
    getOperationalCapabilities(currentUser);
  const actionVisibility = getAlertActionVisibility(alert, currentUser?.role, {
    canManageOrders,
    canManageCheckins,
  });
  const alertActionMode = getAlertActionMode(alert, currentUser?.role);
  const isActionableReturnAlert =
    isReturnAlert && actionVisibility.showWorkflowActions;
  const currentItem = checkin?.items?.find(
    (item) => item.id === alert.checkin_item_id,
  );
  const returnDecisionStillPending =
    currentItem?.status === "DECISION" &&
    currentItem.return_status === "DECISION" &&
    !!selectLatestActiveReturnLot(currentItem.return_lots);
  const returnDecisionAvailable =
    isCheckinFresh && !isCheckinError && returnDecisionStillPending;
  useEffect(() => {
    if (
      !returnResolutionOpen ||
      isCheckinError ||
      !isCheckinFresh ||
      returnDecisionStillPending
    )
      return;
    setReturnResolutionOpen(false);
    onNestedDialogChange?.(false);
  }, [
    returnResolutionOpen,
    isCheckinError,
    isCheckinFresh,
    returnDecisionStillPending,
    onNestedDialogChange,
  ]);
  const isBusy =
    updateStatus.isPending ||
    updateNote.isPending ||
    approveCR.isPending ||
    rejectCR.isPending ||
    decideReturn.isPending ||
    bulkPending ||
    (isActionableReturnAlert && (isCheckinLoading || !currentItem));

  async function trackDetailMutation<T>(action: () => Promise<T>): Promise<T> {
    onMutationPendingChange?.(true);
    try {
      return await action();
    } finally {
      onMutationPendingChange?.(false);
    }
  }

  async function runStatusTransition(
    nextStatus: AlertStatus,
    action: () => Promise<void>,
  ): Promise<boolean> {
    const dismiss = shouldDismiss(activeStatusFilter, nextStatus);
    setError(null);
    try {
      await action();
      if (dismiss) {
        setIsDismissing(true);
        await sleep(180);
      }
      return true;
    } catch (e) {
      setError(notificationActionError(e, alert));
      return false;
    }
  }

  async function runReturnResolution(action: () => Promise<void>) {
    const dismiss = shouldDismiss(activeStatusFilter, "solved");
    setError(null);
    try {
      await action();
      if (dismiss) {
        setIsDismissing(true);
        await sleep(180);
      }
    } catch (e) {
      setError(notificationActionError(e, alert));
      throw e;
    }
  }

  async function handleMarkSeen() {
    if (bulkPending) return false;
    const nextStatus: AlertStatus = getSeenTransitionStatus();
    return trackDetailMutation(() =>
      runStatusTransition(nextStatus, async () => {
        await updateStatus.mutateAsync({ id: alert.id, status: nextStatus });
      }),
    );
  }

  async function handleApprove() {
    const changeRequestId = alert.change_request_id;
    if (!changeRequestId) return;
    await trackDetailMutation(() =>
      runStatusTransition("solved", async () => {
        await approveCR.mutateAsync(changeRequestId);
        onResolutionSuccess?.("Solicitação aprovada.");
      }),
    );
  }

  async function handleReject() {
    const changeRequestId = alert.change_request_id;
    if (!changeRequestId || !rejectReason.trim()) return;
    await trackDetailMutation(() =>
      runStatusTransition("solved", async () => {
        await rejectCR.mutateAsync({
          crId: changeRequestId,
          payload: { rejection_reason: rejectReason.trim() },
        });
        setRejectOpen(false);
        onResolutionSuccess?.("Solicitação recusada.");
      }),
    );
  }

  async function handleSaveNote() {
    const trimmedNote = noteText.trim();
    if (!trimmedNote) return;
    setError(null);
    try {
      await trackDetailMutation(() =>
        updateNote.mutateAsync({ id: alert.id, note: trimmedNote }),
      );
      setNoteOpen(false);
    } catch (e) {
      setError(notificationActionError(e, alert));
    }
  }

  function openAlertDestination() {
    const destination = getAlertDestination(alert, currentUser?.role);
    if (!destination) return;
    navigate(destination);
  }

  async function openInformationalTarget() {
    if (alert.status === "sent" && !(await handleMarkSeen())) return;
    openAlertDestination();
  }

  return (
    <div
      style={{
        maxHeight: isDismissing ? 0 : 720,
        opacity: isDismissing ? 0 : 1,
        transform: isDismissing ? "translateY(-10px)" : "translateY(0)",
        overflow: "hidden",
        transition:
          "max-height 0.22s ease, opacity 0.18s ease, transform 0.18s ease",
      }}
    >
      <div
        data-testid="notification-alert-row"
        style={{
          padding: "0.95rem 1rem",
          borderBottom: `1px solid ${operationalTheme.border}`,
          borderLeft: isSolved
            ? "2px solid color-mix(in srgb, #16a34a 48%, transparent)"
            : "2px solid transparent",
          opacity: isSolved ? 0.72 : 1,
          transition: "opacity 0.18s ease, border-color 0.18s ease",
          background: "var(--notification-table-surface, var(--login-surface))",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "0.625rem",
            marginBottom: "0.375rem",
          }}
        >
          {!summaryInHeader && (
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: PRIORITY_COLORS[alert.priority],
                flexShrink: 0,
                marginTop: "6px",
              }}
            />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.375rem",
                marginBottom: "0.3rem",
                flexWrap: "wrap",
              }}
            >
              {!summaryInHeader && (
                <span
                  style={{
                    fontSize: "0.6875rem",
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    color: "var(--login-accent)",
                    background:
                      "color-mix(in srgb, var(--login-accent) 12%, transparent)",
                    padding: "1px 6px",
                    borderRadius: "4px",
                  }}
                >
                  {getAlertTypeLabel(alert.alert_type)}
                </span>
              )}
              <span
                className="notification-alert-status"
                data-status={alert.status}
                data-actionable={alert.actionable || undefined}
              >
                {alert.status === "sent"
                  ? alert.actionable
                    ? "Pendente"
                    : "Nova"
                  : alert.status === "seen"
                    ? "Vista"
                    : "Resolvida"}
              </span>
              <span
                style={{
                  fontSize: "0.6875rem",
                  color: "var(--login-muted)",
                  marginLeft: "auto",
                  flexShrink: 0,
                }}
              >
                {timeAgo(alert.created_at)}
              </span>
            </div>

            {!summaryInHeader &&
              (() => {
                const lines = alert.message.split("\n").filter(Boolean);
                if (lines.length > 1) {
                  return (
                    <div
                      style={{
                        margin: 0,
                        lineHeight: 1.45,
                        wordBreak: "break-word",
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: "var(--login-text)",
                        }}
                      >
                        {lines[0]}
                      </p>
                      {lines.slice(1).map((line, i) => (
                        <p
                          key={i}
                          style={{
                            margin: 0,
                            fontSize: "0.8125rem",
                            fontWeight: 400,
                            color: "var(--login-muted)",
                            marginTop: "0.125rem",
                          }}
                        >
                          {line}
                        </p>
                      ))}
                    </div>
                  );
                }

                return (
                  <p
                    style={{
                      margin: 0,
                      fontSize: "0.875rem",
                      color: "var(--login-text)",
                      lineHeight: 1.45,
                      wordBreak: "break-word",
                      whiteSpace: "pre-line",
                    }}
                  >
                    {alert.message}
                  </p>
                );
              })()}

            {(alert.seen_at ||
              alert.solved_at ||
              alert.note ||
              alert.request_note ||
              alert.decision_note) && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.2rem",
                  marginTop: "0.5rem",
                  paddingTop: "0.375rem",
                  borderTop: "1px dashed var(--login-border)",
                }}
              >
                {alert.seen_at && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.3rem",
                      fontSize: "0.6875rem",
                      color: "var(--login-muted)",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    <Eye size={11} />
                    Vista {timeAgo(alert.seen_at)}
                  </div>
                )}
                {alert.solved_at && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.3rem",
                      fontSize: "0.6875rem",
                      color: "#16a34a",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    <CheckCheck size={11} />
                    Resolvida {timeAgo(alert.solved_at)}
                  </div>
                )}
                {alert.request_note && (
                  <div
                    className="notification-request-note"
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "0.3rem",
                      fontSize: "0.6875rem",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    <MessageSquare
                      size={11}
                      style={{ flexShrink: 0, marginTop: "1px" }}
                    />
                    <span style={{ wordBreak: "break-word" }}>
                      Motivo do checker: {alert.request_note}
                    </span>
                  </div>
                )}
                {alert.decision_note && (
                  <div
                    className="notification-decision-note"
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "0.3rem",
                      fontSize: "0.6875rem",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    <MessageSquare
                      size={11}
                      style={{ flexShrink: 0, marginTop: "1px" }}
                    />
                    <span style={{ wordBreak: "break-word" }}>
                      Nota da decisão: {alert.decision_note}
                    </span>
                  </div>
                )}
                {alert.note && !noteOpen && !isReturnAlert && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "0.3rem",
                      fontSize: "0.6875rem",
                      color: "var(--login-accent)",
                      fontFamily: "'DM Sans', sans-serif",
                      fontStyle: "italic",
                    }}
                  >
                    <MessageSquare
                      size={11}
                      style={{ flexShrink: 0, marginTop: "1px" }}
                    />
                    <span style={{ wordBreak: "break-word" }}>
                      {alert.note}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {error && (
          <p className="notification-action-error" role="alert">
            {error}
          </p>
        )}
        {isActionableReturnAlert && isCheckinError ? (
          <div
            role="alert"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              margin: "0.25rem 0",
              fontSize: "0.75rem",
              color: "#ef4444",
            }}
          >
            <span>Não foi possível carregar a conferência.</span>
            <InlineActionButton
              tone="neutral"
              onClick={() => void refetchCheckin()}
            >
              Tentar novamente
            </InlineActionButton>
          </div>
        ) : null}

        {isActionableReturnAlert &&
        !!checkin &&
        !isCheckinFresh &&
        !isCheckinError ? (
          <div className="notification-action-hint" role="status">
            Atualizando carga...
          </div>
        ) : null}

        {isActionableReturnAlert &&
        isCheckinFresh &&
        !isCheckinError &&
        !returnDecisionAvailable ? (
          <div
            className="notification-action-hint"
            role={currentItem ? "status" : "alert"}
          >
            <span>
              {currentItem
                ? "Decisão já registrada. Confira a carga antes de fechar."
                : "Item vinculado não encontrado nesta carga."}
            </span>
            {!currentItem ? (
              <InlineActionButton
                tone="neutral"
                onClick={() => void refetchCheckin()}
              >
                Atualizar carga
              </InlineActionButton>
            ) : null}
          </div>
        ) : null}

        {!isSolved && (
          <div
            style={{
              display: "flex",
              gap: "0.375rem",
              marginTop: "0.625rem",
              flexWrap: "wrap",
              paddingLeft: summaryInHeader ? 0 : "1.25rem",
              justifyContent: summaryInHeader ? "center" : undefined,
            }}
          >
            <AlertActions
              alert={alert}
              alertActionMode={alertActionMode}
              isReturnAlert={isActionableReturnAlert}
              isBusy={isBusy}
              canEditNote={actionVisibility.canEditNote}
              canMarkSeen={actionVisibility.canMarkSeen}
              canManageChangeRequest={actionVisibility.canManageChangeRequest}
              canReviewProduct={actionVisibility.canReviewProduct}
              canOpenSupplierPriceList={
                actionVisibility.canOpenSupplierPriceList
              }
              canOpenReportWorkspace={actionVisibility.canOpenReportWorkspace}
              canReplaceReturn={
                actionVisibility.canReplaceReturn && returnDecisionAvailable
              }
              canCancelReturn={
                actionVisibility.canCancelReturn && returnDecisionAvailable
              }
              onOpenTargetOrder={
                alert.alert_type === "report_pedido_due"
                  ? () => void openInformationalTarget()
                  : openAlertDestination
              }
              onToggleSubstitution={() => {
                setReturnResolutionDecision("SUBSTITUIDO");
                setReturnResolutionOpen(true);
                onNestedDialogChange?.(true);
              }}
              onCancelReturn={() => {
                setReturnResolutionDecision("DEVOLVIDO");
                setReturnResolutionOpen(true);
                onNestedDialogChange?.(true);
              }}
              onApproveChangeRequest={() => void handleApprove()}
              onToggleReject={() => setRejectOpen((value) => !value)}
              onMarkSeen={() => void handleMarkSeen()}
              onOpenProductReview={openAlertDestination}
              onOpenSupplierPriceList={openAlertDestination}
              onOpenReportWorkspace={
                alert.alert_type === "report_mensal_due" ||
                alert.alert_type === "report_pedido_stale"
                  ? () => void openInformationalTarget()
                  : openAlertDestination
              }
              onToggleNote={() => {
                setNoteOpen((value) => !value);
                setNoteText(alert.note ?? "");
              }}
            />
          </div>
        )}

        {isActionableReturnAlert && checkin && currentItem ? (
          <ReturnResolutionModal
            appearance="notification"
            open={returnResolutionOpen}
            item={currentItem}
            allowReplacement={actionVisibility.canReplaceReturn}
            allowCancellation={actionVisibility.canCancelReturn}
            available={
              actionVisibility.canCancelReturn && returnDecisionStillPending
            }
            initialDecision={returnResolutionDecision}
            pending={isBusy}
            onClose={() => {
              setReturnResolutionOpen(false);
              onNestedDialogChange?.(false);
            }}
            onResolve={async (payload) => {
              const checkinItemId = alert.checkin_item_id;
              if (!checkinItemId) {
                throw new Error("return decision is no longer available");
              }
              await trackDetailMutation(() =>
                runReturnResolution(async () => {
                  await decideReturn.mutateAsync({
                    itemId: checkinItemId,
                    ...payload,
                  });
                }),
              );
            }}
            onSuccess={onResolutionSuccess}
          />
        ) : null}

        {isSolved && !isReturnAlert && actionVisibility.canEditNote && (
          <div
            style={{
              marginTop: "0.5rem",
              paddingLeft: summaryInHeader ? 0 : "1.25rem",
              textAlign: summaryInHeader ? "center" : undefined,
            }}
          >
            <InlineActionButton
              onClick={() => {
                setNoteOpen((value) => !value);
                setNoteText(alert.note ?? "");
              }}
              disabled={isBusy}
              icon={<MessageSquare size={12} />}
            >
              {alert.note ? "Editar nota" : "Nota"}
            </InlineActionButton>
          </div>
        )}

        <div
          style={{
            ...COLLAPSE_STYLE,
            maxHeight: noteOpen ? 240 : 0,
            opacity: noteOpen ? 1 : 0,
            transform: noteOpen ? "translateY(0)" : "translateY(-6px)",
            pointerEvents: noteOpen ? "auto" : "none",
          }}
        >
          <div
            style={{
              marginTop: "0.5rem",
              paddingLeft: summaryInHeader ? 0 : "1.25rem",
            }}
          >
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Adicionar nota interna..."
              rows={2}
              style={{
                width: "100%",
                padding: "0.5rem",
                background:
                  "var(--notification-table-surface, var(--login-bg))",
                border:
                  "1px solid var(--notification-table-border, var(--login-border))",
                borderRadius: "0.375rem",
                color: "var(--login-text)",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.875rem",
                resize: "vertical",
                boxSizing: "border-box",
              }}
            />
            <div
              style={{
                display: "flex",
                gap: "0.375rem",
                marginTop: "0.375rem",
                justifyContent: summaryInHeader ? "center" : undefined,
              }}
            >
              <InlineActionButton
                onClick={handleSaveNote}
                disabled={!noteText.trim() || isBusy}
              >
                Salvar nota
              </InlineActionButton>
              <InlineActionButton
                tone="neutral"
                onClick={() => setNoteOpen(false)}
                disabled={isBusy}
              >
                Cancelar
              </InlineActionButton>
            </div>
          </div>
        </div>

        <div
          style={{
            ...COLLAPSE_STYLE,
            maxHeight:
              rejectOpen && actionVisibility.canManageChangeRequest ? 240 : 0,
            opacity:
              rejectOpen && actionVisibility.canManageChangeRequest ? 1 : 0,
            transform:
              rejectOpen && actionVisibility.canManageChangeRequest
                ? "translateY(0)"
                : "translateY(-6px)",
            pointerEvents:
              rejectOpen && actionVisibility.canManageChangeRequest
                ? "auto"
                : "none",
          }}
        >
          <div
            style={{
              marginTop: "0.5rem",
              paddingLeft: summaryInHeader ? 0 : "1.25rem",
            }}
          >
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Motivo da recusa..."
              rows={2}
              style={{
                width: "100%",
                padding: "0.5rem",
                background:
                  "var(--notification-table-surface, var(--login-bg))",
                border:
                  "1px solid var(--notification-table-border, var(--login-border))",
                borderRadius: "0.375rem",
                color: "var(--login-text)",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.875rem",
                resize: "vertical",
                boxSizing: "border-box",
              }}
            />
            <div
              style={{
                display: "flex",
                gap: "0.375rem",
                marginTop: "0.375rem",
                justifyContent: summaryInHeader ? "center" : undefined,
              }}
            >
              <InlineActionButton
                tone="danger"
                onClick={handleReject}
                disabled={!rejectReason.trim() || isBusy}
              >
                Confirmar recusa
              </InlineActionButton>
              <InlineActionButton
                tone="neutral"
                onClick={() => setRejectOpen(false)}
                disabled={isBusy}
              >
                Cancelar
              </InlineActionButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
